import { useEffect, useRef } from "react";

import "./ConfirmDialog.css";

/**
 * Confirmation for an action that changes someone else's access.
 *
 * Shown for every role change, enable/disable, account creation, and
 * invitation. None of these can be undone by pressing back: disabling an
 * account cuts a session off mid-use, and an invitation sends real email.
 *
 * This is a UI courtesy and nothing more. The backend's own guards — an admin
 * cannot change their own role or status, and the last active admin cannot be
 * demoted or disabled — hold whether or not this dialog was shown.
 *
 * @param {{request: {title: string, body: string, confirmLabel: string, tone?: string}|null, onConfirm: () => void, onCancel: () => void}} props
 */
function ConfirmDialog({ request, onConfirm, onCancel }) {
  const confirmRef = useRef(null);

  // Escape cancels, and focus lands on the confirm button so the dialog is
  // operable from the keyboard alone.
  useEffect(() => {
    if (!request) {
      return undefined;
    }

    confirmRef.current?.focus();

    function onKeyDown(event) {
      if (event.key === "Escape") {
        onCancel();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [request, onCancel]);

  if (!request) {
    return null;
  }

  const isDanger = request.tone === "danger";

  return (
    <div className="confirm-backdrop" onClick={onCancel}>
      <div
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-body"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="confirm-dialog-title">{request.title}</h2>
        <p id="confirm-dialog-body">{request.body}</p>

        <div className="confirm-actions">
          <button type="button" className="confirm-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            ref={confirmRef}
            className={`confirm-btn ${isDanger ? "is-danger" : "is-primary"}`}
            onClick={onConfirm}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
